// W3: PostHog accessibility diagnostic — boot reachability probe plus a
// continuous xhr_error hook on the SDK. Both layers route through the W1
// `forwardLog` IPC bridge so any failure surfaces in the Electron loggy /
// flume pipeline. The event-name constants below are the stable contract
// (acceptance #3) so future readers can grep for them.
//
// Per rule: stay-in-scope (ai.knowledge/global) — engineer scope freeze:
// errors+warnings only, browser-side accessibility check, host read from
// the existing PostHogConfig.api_host field (no new config knob).
// Per rule: think-in-systems (ai.knowledge/global) — single-purpose module
// with two narrow exports; manager.ts wires them in at one init point.
// Per rule: ui-architecture (ai.knowledge/global) — renderer TypeScript only.
// Per rule: reference-rules-in-comments (ai.knowledge/global).

import type { PostHog } from 'posthog-js';
import { forwardLog } from '#~/logger/forward.js';

// Stable event-name contract. Documented so callers / log searchers can
// match on these literals without digging into the implementation.
export const INGEST_UNREACHABLE_MESSAGE = 'posthog.ingest.unreachable';
export const INGEST_ERROR_MESSAGE = 'posthog.ingest.error';

// Idempotency gate: re-init of PostHogManager must not stack duplicate
// xhr_error hooks on the same instance. Mirrors W2's intercept.ts WeakSet
// pattern so the two hooks share the same lifecycle discipline.
const ATTACHED_INSTANCES = new WeakSet<PostHog>();

// 3s timeout per spec — short enough to keep boot non-blocking, long
// enough to clear a slow DNS lookup on a degraded network.
const PROBE_TIMEOUT_MS = 3000;

/**
 * Fire a single HEAD probe against the configured PostHog ingest host and
 * forward a warn-level record if the request fails or times out. Repeat
 * invocations re-probe (idempotent — no crash on re-init, no listener
 * stacking because no listener is installed here).
 *
 * Skips silently when `host` is empty / non-string (e.g. dev build with
 * no PostHog key configured) per acceptance #6.
 */
export async function runBootReachabilityProbe(host: string): Promise<void> {
  // Silent skip — diagnostic must not crash and must not log when there
  // is no host configured. This is the common dev path with no PostHog key.
  if (!host || typeof host !== 'string') return;

  // Derive hostname for the structured context. Fall back to the raw host
  // string if URL parsing fails — never throw out of this function.
  let hostname: string;
  try {
    hostname = new URL(host).hostname;
  } catch {
    hostname = host;
  }

  // HEAD avoids downloading the response body. AbortController + setTimeout
  // bounds the worst case so a hung ingest never delays our pipeline.
  // We do NOT use AbortSignal.timeout() because JSDOM (the test environment
  // for studio.client) does not implement that static method — every call
  // would throw before fetch is reached. AbortController + setTimeout is
  // universally available in Node/browser/JSDOM and behaves identically.
  // Per rule: ai-code-requires-tests (ai.knowledge/global) — the JSDOM gap
  // was the root cause of QA iter-1's diagnostic test failures.
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(host, {
      method: 'HEAD',
      signal: ac.signal
    });
  } catch (err) {
    // Fire-and-forget warn forward. Any failure (timeout, DNS, TLS, CORS,
    // network down) lands as a single record with hostname + stringified
    // error for triage.
    forwardLog({
      source: 'posthog',
      level: 'warn',
      message: INGEST_UNREACHABLE_MESSAGE,
      context: {
        hostname,
        error: String(err)
      }
    });
  } finally {
    // Always release the timer — on success we don't want a delayed abort
    // firing against the next fetch; on failure clearTimeout is a no-op
    // if the timer already ran but is still cheap insurance.
    clearTimeout(timer);
  }
}

/**
 * Install a continuous xhr_error hook on the live PostHog SDK config so
 * every failed ingest XHR forwards a warn-level record via the W1 bridge.
 *
 * The SDK exposes `config.on_xhr_error` as a mutable callback (see
 * posthog-js/src/posthog-core.ts). Setting it post-init works because the
 * SDK reads `config.on_xhr_error` at request time, not init time. If the
 * SDK ever drops this surface, the hook simply doesn't fire — feature
 * detection below avoids a crash on version drift.
 *
 * Idempotent: a WeakSet keyed on the PostHog instance ensures repeat
 * `attachIngestErrorHook` calls never wrap the existing handler twice
 * (which would double-forward every failure).
 */
export function attachIngestErrorHook(posthog: PostHog): void {
  // Defensive: no-op when the caller passes a null/undefined instance
  // (manager.ts guards on this.posthogInstance, but we re-check here so
  // direct test callers can pass anything without crashing).
  if (!posthog) return;

  // Idempotency gate — same instance re-attach is a silent no-op.
  if (ATTACHED_INSTANCES.has(posthog)) return;
  ATTACHED_INSTANCES.add(posthog);

  // Keep the no-config fallback for partial test instances; the SDK declares config publicly.
  const phWithConfig = posthog;
  if (!phWithConfig.config) return;

  // Preserve any handler the SDK or another caller already installed so
  // we compose instead of clobbering — a defensive habit lifted from
  // W2's console-wrap pattern (call original handler after forwarding).
  const existing = phWithConfig.config.on_xhr_error;

  phWithConfig.config.on_xhr_error = (failedRequest: XMLHttpRequest) => {
    try {
      forwardLog({
        source: 'posthog',
        level: 'warn',
        message: INGEST_ERROR_MESSAGE,
        context: {
          status: failedRequest?.status,
          statusText: failedRequest?.statusText,
          responseURL: failedRequest?.responseURL
        }
      });
    } catch {
      // Never let the diagnostic surface as a user-visible error.
    }

    // Re-invoke whatever handler was already there — preserves any SDK
    // internal retry logic or app-level instrumentation.
    if (typeof existing === 'function') {
      try {
        existing(failedRequest);
      } catch {
        // Original handler's bugs are not ours to surface.
      }
    }
  };
}
